import { useMemo, useState } from 'react'

import { useSession } from '@/shared/services/session/compat'

import { useUserProgress } from '@/shared/hooks/useUserProgress'
import {
  effectiveFilter,
  groupByStatus,
  visibleFilterOptions
} from '@/shared/lib/list-view'
import { useGamificationDataStore } from '@/shared/store/gamificationDataStore'
import { usePrizesStore } from '@/shared/store/usePrizesStore'
import type { Prize } from '@/types/prizes'

import {
  PRIZE_SORT_PRIORITY,
  PRIZE_STATUS_LABELS,
  type PrizeFilterId,
  type PrizeUiStatus
} from '../constants/status'
import { PRIZE_FILTER_OPTIONS } from '../lib/prizeFilterOptions'
import { getPrizeIneligibilityHint } from '../lib/prizeRuleMessage'
import { getPrizeUiStatus, isPrizeWinner } from '../lib/prizeUiStatus'
import { selectFeaturedPrize } from '../lib/selectFeaturedPrize'

export type PrizeListItem = Prize & {
  uiStatus: PrizeUiStatus
  isParticipating: boolean
  isWinner: boolean
  ineligibilityHint: string | null
}

const matchesFilter = (item: PrizeListItem, filter: PrizeFilterId): boolean => {
  if (filter === 'all') return true
  return item.uiStatus === filter
}

export const usePrizesListView = () => {
  const { prizes: rawPrizes } = usePrizesStore()
  const { participatingPrizes } = useUserProgress()
  const { version } = useGamificationDataStore(s => s.getActiveProductData())
  const { data: session } = useSession()
  const [rawFilter, setFilter] = useState<PrizeFilterId>('all')

  const items = useMemo((): PrizeListItem[] => {
    // Por identificador, e não por e-mail: o serializer público deixou de
    // emitir e-mail quando o payload foi reduzido por privacidade.
    //
    // E sobre a **coleção** de ganhadores, não sobre um campo único: um
    // concurso pode ter vários, sorteados em rodadas.
    const userId = session?.user?.id

    return rawPrizes
      .filter(prize => !prize.hidden)
      .map(prize => {
        const isParticipating = participatingPrizes.includes(prize.id)
        const isWinner = isPrizeWinner(prize, userId)
        const uiStatus = getPrizeUiStatus(prize, { isParticipating, isWinner })
        const ineligibilityHint =
          uiStatus === 'locked'
            ? getPrizeIneligibilityHint(prize.prize_rules, prize.partner)
            : null

        return {
          ...prize,
          uiStatus,
          isParticipating,
          isWinner,
          ineligibilityHint
        }
      })
  }, [rawPrizes, participatingPrizes, session?.user?.id])

  const visibleFilters = useMemo(
    () =>
      visibleFilterOptions(PRIZE_FILTER_OPTIONS, id =>
        items.some(item => matchesFilter(item, id))
      ),
    [items]
  )

  const filter = effectiveFilter(rawFilter, visibleFilters)

  const sortedItems = useMemo(() => {
    const filtered = items.filter(item => matchesFilter(item, filter))

    return [...filtered].sort((a, b) => {
      const priority =
        PRIZE_SORT_PRIORITY[a.uiStatus] - PRIZE_SORT_PRIORITY[b.uiStatus]

      if (priority !== 0) return priority

      return a.id - b.id
    })
  }, [items, filter])

  const featuredPrize = useMemo(() => selectFeaturedPrize(items), [items])

  const globalProgress = useMemo(() => {
    const active = items.filter(item => !item.executed)
    const participating = active.filter(item => item.uiStatus === 'progress')
    const total = active.length
    const participatingCount = participating.length
    const percent =
      total > 0 ? Math.round((participatingCount / total) * 100) : 0

    return {
      activeCount: total,
      participatingCount,
      percent,
      wonCount: items.filter(item => item.uiStatus === 'success').length
    }
  }, [items])

  /* Prêmios exibem os ganhos PRIMEIRO — divergência declarada em
     `PRIZE_SORT_PRIORITY` via `resolveOrder({ success: -1 })`, não numa segunda
     tabela. O destaque sai do agrupamento para não duplicar. */
  const groups = useMemo(() => {
    if (filter !== 'all') return []

    const featuredId = featuredPrize?.id

    return groupByStatus(items, {
      order: PRIZE_SORT_PRIORITY,
      labels: PRIZE_STATUS_LABELS,
      statusOf: item => item.uiStatus,
      exclude: item => !!featuredId && item.id === featuredId
    })
  }, [filter, items, featuredPrize])

  return {
    filter,
    setFilter,
    items: sortedItems,
    allItems: items,
    visibleFilters,
    groups,
    featuredPrize,
    globalProgress,
    isEmpty: items.length === 0,
    isFilteredEmpty: sortedItems.length === 0 && items.length > 0,
    isLoading: version === ''
  }
}
